'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

export default function Login() {
  const router = useRouter()
  const [step, setStep] = useState<'EMAIL' | 'PASSWORD' | 'REGISTER'>('EMAIL')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  
  // Quick Registration fields
  const [regFirstName, setRegFirstName] = useState('')
  const [regLastName, setRegLastName] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [regPhone, setRegPhone] = useState('')
  const [honeypot, setHoneypot] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Fehler beim Überprüfen')
      }

      if (data.exists) {
        setStep('PASSWORD')
      } else {
        // No account yet: offer quick account registration directly
        setStep('REGISTER')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  
  const handleForgotPassword = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler beim Senden')
      alert('Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen gesendet.')
      setStep('EMAIL')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Fehler beim Anmelden')
      }

      if (data.user.role === 'ADMIN') {
        window.location.href = '/dashboard/admin'
      } else if (data.user.role === 'TRAINER') {
        window.location.href = '/dashboard/trainer'
      } else {
        window.location.href = '/dashboard'
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRegisterAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (regPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.')
      setLoading(false)
      return
    }

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountOnly: true,
          email,
          guardianFirstName: regFirstName,
          guardianLastName: regLastName,
          password: regPassword,
          honeypot,
          mobile: regPhone
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Fehler bei der Registrierung')
      }

      if (data.requiresVerification) {
        setStep('EMAIL')
        setError('')
        alert(data.message || 'Bitte überprüfe dein E-Mail-Postfach und klicke auf den Bestätigungslink, um dein Konto zu aktivieren.')
        return
      }

      // Successfully registered and logged in -> redirect to dashboard
      window.location.href = '/dashboard'
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '400px' }}>
        <div className="auth-container">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img src="/logo.png" alt="Sportschule Dittus" style={{ width: '150px', height: 'auto', display: 'block', margin: '0 auto' }} />
          </div>
          <h1 className="auth-title">Willkommen</h1>
          <p className="auth-subtitle">Sportschule Dittus</p>
          
          {error && <div className="alert-error">{error}</div>}
          
          {step === 'EMAIL' && (
            <form onSubmit={handleEmailCheck}>
              {/* HONEYPOT - DO NOT REMOVE */}
              <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label>Website</label>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              {/* END HONEYPOT */}

              <div className="form-group">
                <label htmlFor="email">E-Mail-Adresse</label>
                <input 
                  id="email"
                  type="email" 
                  className="form-input" 
                  placeholder="deine.email@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                  Gib deine E-Mail-Adresse ein, um dich anzumelden oder ein neues Benutzerkonto zu erstellen.
                </small>
              </div>
              <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Prüfe...' : 'Weiter'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
                <button
                  type="button"
                  onClick={() => setStep('REGISTER')}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}
                >
                  Noch kein Konto? Jetzt registrieren
                </button>
              </div>
            </form>
          )}

          {step === 'PASSWORD' && (
            <form onSubmit={handleLogin}>
              {/* HONEYPOT - DO NOT REMOVE */}
              <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label>Website</label>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              {/* END HONEYPOT */}

              <div className="form-group">
                <label htmlFor="email">E-Mail-Adresse</label>
                <input 
                  id="email"
                  type="email" 
                  className="form-input" 
                  value={email}
                  disabled
                  style={{ opacity: 0.6 }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Passwort</label>
                <input 
                  id="password"
                  type="password" 
                  className="form-input" 
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Lade...' : 'Einloggen'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                <button 
                  type="button" 
                  onClick={() => { setStep('EMAIL'); setError(''); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  &larr; Zurück
                </button>
                <button 
                  type="button" 
                  onClick={handleForgotPassword}
                  style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  Passwort vergessen?
                </button>
              </div>
            </form>
          )}

          {step === 'REGISTER' && (
            <form onSubmit={handleRegisterAccount}>
              {/* HONEYPOT - DO NOT REMOVE */}
              <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label>Website</label>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              {/* END HONEYPOT */}

              <div style={{ marginBottom: '1rem', background: 'rgba(37, 99, 235, 0.08)', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(37, 99, 235, 0.2)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                  ✨ <strong>Neues Benutzerkonto anlegen</strong><br/>
                  Du kannst dich registrieren und danach im Dashboard flexibel ein Abo für dich selbst buchen oder dein Kind anmelden.
                </span>
              </div>

              <div className="form-group">
                <label>E-Mail-Adresse</label>
                <input 
                  type="email" 
                  className="form-input" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine.email@beispiel.de"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Vorname</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={regFirstName}
                    onChange={(e) => setRegFirstName(e.target.value)}
                    placeholder="Max"
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Nachname</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={regLastName}
                    onChange={(e) => setRegLastName(e.target.value)}
                    placeholder="Mustermann"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Handy- / Telefonnummer</label>
                <input 
                  type="tel" 
                  className="form-input" 
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  placeholder="0170 1234567"
                />
              </div>

              <div className="form-group">
                <label>Passwort wählen (mind. 6 Zeichen)</label>
                <input 
                  type="password" 
                  className="form-input" 
                  placeholder="••••••••"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Konto wird erstellt...' : 'Konto erstellen & zum Dashboard'}
              </button>

              <button 
                type="button" 
                onClick={() => { setStep('EMAIL'); setError(''); }}
                style={{ marginTop: '1rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', width: '100%', cursor: 'pointer' }}
              >
                Bereits ein Konto? Zurück zur Anmeldung
              </button>
            </form>
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem' }}>
          <a href="https://dittus-mse.de/#impressum" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none', marginRight: '1.5rem' }}>Impressum</a>
          <a href="https://dittus-mse.de/#datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: '#888', textDecoration: 'none' }}>Datenschutz</a>
        </div>
      </div>
    </main>
  )
}
